import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concat, of, timer } from 'rxjs';
import { catchError, map, tap, withLatestFrom, mergeMap, filter, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { cartCalculationProcessor, cartStateProcessor, clearCartForStore, prepareCartItemRemoval, prepareCartStockRefresh } from '@core/domain/cart';
import {
	CartService,
	IntegerPrecisionService,
	MenuItemService,
	StoreService,
} from '@core/services';
import { homeProcessor } from '@/features/home/domain';
import { v4 as uuidv4 } from 'uuid';
import {
	CART_CLEAR,
	CART_FAILED,
	CART_FIND_SUCCESS,
	CART_FIND_TOTALS,
	CART_PRODUCT_ORDER_PROCESS,
	CART_PRODUCT_ORDER_PROCESS_FAILURE,
	CART_PRODUCT_ORDER_PROCESS_SUCCESS,
	CART_REMOVE,
	CART_UPDATE,
	CART_UPDATE_STATE,
	CART_UPDATE_STOCK,
	CART_UPDATE_STOCK_ALL,
	CART_UPDATE_TOTALS,
	COUPON_RESET,
	NOTIFICATION_SHOW,
	PRODUCT_FIND_SUCCESS,
	WORK_DAY_FIND_SUCCESS,
	CART_UPDATE_STOCK_ALL_SUCCESS,
	COUPON_VALID
} from '@store/actions';
import {
	selectCart,
	selectCartList,
	selectCoupon,
	selectDeliveryZone,
	selectLocalChannelId,
	selectLocalChannelSesion,
	selectMenu,
	selectOrderType,
	selectProductList,
	selectSlug,
	selectTipDetail,
	selectWorkDay,
} from '@store/selectors';
import { CartProcessorService } from '@/features/shoppingcart/domain';
import { Coupon } from '@/core/models';
import { selectAppliedCoupon } from '../selectors/coupon.selectors';


@Injectable()
export class CartEffects {
	constructor(
		private store: Store,
		private actions$: Actions,
		private storeService: StoreService,
		private integerPrecisionService: IntegerPrecisionService,
		private menuItemService: MenuItemService,
		private cartService: CartService,
		private cartProcessor: CartProcessorService
	) {}

  	creationComplete$ = createEffect(() =>
  	  this.actions$.pipe(
  	    ofType(CART_UPDATE_STOCK_ALL_SUCCESS, CART_FAILED),
  	    tap(() => {
  	      this.cartService.updateAllStockCompleted();
  	    })
  	  ),
  	  { dispatch: false }
  	)

	updateAllStock$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CART_UPDATE_STOCK_ALL),
			withLatestFrom(
				this.store.select(selectCartList),
				this.store.select(selectLocalChannelId),
				this.store.select(selectMenu),
			),
			map(([action, cartItems, localChannelId]) =>
				prepareCartStockRefresh({ action, cartItems, localChannelId })
			),
			mergeMap(context =>
				this.menuItemService
					.getMenuItemList(
						context.articleIds,
						null,
						null,
						context.localChannelId,
						context.menuId
					)
					.pipe(
						mergeMap(response =>
							[
								CART_FIND_SUCCESS({items: this.cartProcessor.syncMenuItemStock(context.cartItems, response.data)}),
								CART_UPDATE_STOCK_ALL_SUCCESS({itemsUpdated: response.data})
							]
						),
						catchError(error => of(CART_FAILED({ error })))
					)
			)
		)
	);

  updateState$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CART_UPDATE_STATE),
			withLatestFrom(
				this.store.select(selectCartList),
				this.store.select(selectSlug)
			),
			filter(([, , slug]) => !!slug),
			map(([action, cartItems, storeSlug]) => ({ action, cartItems, storeSlug: storeSlug as string })),
			mergeMap(({ action, cartItems, storeSlug }) =>
				of(
					CART_FIND_SUCCESS({
						items: cartStateProcessor.upsertCartItemFromEffect({
							action,
							cartItems,
							storeSlug,
						}),
					}),
					CART_UPDATE()
				)
			)
		)
	);

  updateStock$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CART_UPDATE_STOCK),
			withLatestFrom(
				this.store.select(selectProductList),
				this.cartService.getStoreForStore()
			),
			map(([action, productList, cartItems]) =>
				cartStateProcessor.updateStockFromEffect({ action, productList, cartItems })
			),
			mergeMap(result =>
				of(
					CART_FIND_SUCCESS({ items: result.cartItems }),
					PRODUCT_FIND_SUCCESS({ productList: result.productList }),
					CART_UPDATE()
				)
			)
		)
	);

  clearCart$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CART_CLEAR),
			withLatestFrom(this.store.select(selectCartList), this.store.select(selectSlug)),
			filter(([, , slug]) => !!slug),
			mergeMap(([_action, cartItems, slug]) =>
				of(
					CART_FIND_SUCCESS({
						items: clearCartForStore(cartItems, slug as string),
					})
				)
			)
		)
	);

  deleteProductState$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CART_REMOVE),
			withLatestFrom(this.store.select(selectCartList)),
			map(([action, cartItems]) =>
				prepareCartItemRemoval(action.idArticulo, cartItems)
			),
			filter(context => context.shouldRemove),
			map(context => CART_FIND_SUCCESS({ items: context.updatedCartItems }))
		)
	);

  calculateTotals$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CART_FIND_TOTALS, WORK_DAY_FIND_SUCCESS),
			withLatestFrom(
				this.cartService.getStoreForStore(),
				this.store.select(selectTipDetail),
				this.store.select(selectWorkDay),
				this.store.select(selectDeliveryZone),
				this.store.select(selectOrderType),
				this.store.select(selectCoupon),
				this.store.select(selectLocalChannelSesion)
			),
			map(([_action, cartItems, tipDetail, workDay, deliveryZone, orderType, coupon, localChannelSession]) => 
				cartCalculationProcessor.calculateFromEffect(
					{
						cartItems,
						tipDetail,
						workDay,
						deliveryZone,
						orderType,
						coupon,
						localChannelSession,
					},
					this.integerPrecisionService
				)
			),
			filter(context => context !== null),
			mergeMap(result => {
				const updateTotals = CART_UPDATE_TOTALS({ cartTotals: result.cartTotals });

				return result.isCouponInvalid
					? of(COUPON_RESET(), updateTotals)
					: of(updateTotals);
			})
		)
	);

	processOrder$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CART_PRODUCT_ORDER_PROCESS),
			withLatestFrom(
				this.store.select(selectCart), 
				this.cartService.getStoreForStore(),
			),
			mergeMap(([action, cart, cartList]) => {
				try {
					const item = homeProcessor.processSingleProductOrder({
						formValues: action.formValues,
						detailsWithQuantity: action.detailsWithQuantity,
						detailsWithQuantityNoAdd: action.detailsWithQuantityNoAdd,
						selectedArticle: cart,
						cartItems: cartList,
						isEditing: action.isEditing,
						generatedCartItemId: uuidv4(),
					});

					return of(
						CART_UPDATE_STATE({ item }),
						NOTIFICATION_SHOW({
							notification: {
								type: 'success',
								title: 'Success',
								detail: action.isEditing
									? 'Edit Cart Success'
									: 'Product add to Cart',
							},
						}),
						CART_PRODUCT_ORDER_PROCESS_SUCCESS({ item })
					);
				} catch (error) {
					return of(
						CART_PRODUCT_ORDER_PROCESS_FAILURE({
							error: error instanceof Error ? error.message : String(error),
						})
					);
				}
			})
		)
	);

	revalidateAppliedCoupon$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CART_FIND_SUCCESS),
			withLatestFrom(
				this.store.select(selectAppliedCoupon),
				this.store.select(selectCoupon),
			),
			map(([_, code, coupon]) => ({
				code,
				coupon,
			})),
			filter(({ code, coupon }) => !!code && !!coupon),
			switchMap(({ code, coupon }) =>
				concat(
					of(CART_FIND_TOTALS()),
					timer(0).pipe(
						map(() =>
							COUPON_VALID({
								coupon: { ...(coupon ?? {}), code: code! } as Coupon,
							})
						)
					)
				)
			)
		)
	);
}
